import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import ImageUploader from '../components/ImageUploader';

const defaults = {
  sectionId: "Künstler*Innen",
  title: "Musiker*innen",
  entries: []
};

export default function ArtistsEditor(){
  const api = useAdminApi();
  const [doc, setDoc] = useState(defaults);
  const [orig, setOrig] = useState(defaults);

  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingIdx, setSavingIdx] = useState(null); // number | null
  const [editingMeta, setEditingMeta] = useState(false);
  const [editingMap, setEditingMap] = useState({}); // { [idx]: boolean }
  const [error, setError] = useState('');

  // overall dirty (meta or any entry)
  const dirtyAll = useMemo(() => JSON.stringify(doc) !== JSON.stringify(orig), [doc, orig]);

  useEffect(()=>{ let alive=true;(async()=>{
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sections/artists');
      if (!alive) return;
      const merged = { ...defaults, ...(res||{}) };
      setDoc(merged);
      setOrig(merged);
    } catch (e) {
      setError(e?.message || 'Load failed');
      setDoc(defaults);
      setOrig(defaults);
    } finally { if (alive) setLoading(false); }
  })(); return ()=>{alive=false}; },[]); // eslint-disable-line

  // ---- helpers ----
  const setEntry = (idx, patch) => {
    setDoc(d => {
      const next = structuredClone(d);
      next.entries[idx] = { ...(next.entries[idx] || {}), ...patch };
      return next;
    });
  };
  const startEdit = (idx) => setEditingMap(m => ({ ...m, [idx]: true }));
  const stopEdit  = (idx) => setEditingMap(m => ({ ...m, [idx]: false }));

  const isItemDirty = (idx) => {
    const a = doc?.entries?.[idx];
    const b = orig?.entries?.[idx];
    return JSON.stringify(a) !== JSON.stringify(b);
  };

  const saveItem = async (idx) => {
    setSavingIdx(idx);
    setError('');
    try {
      const nextDoc = doc; // already in state
      await api.put('/sections/artists', nextDoc);
      // sync orig with doc after successful save
      setOrig(nextDoc);
      stopEdit(idx);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSavingIdx(null);
    }
  };

  const cancelItem = (idx) => {
    // If it's a newly added item (no counterpart in orig), remove it on cancel
    const isNew = idx >= (orig.entries?.length || 0);
    if (isNew) {
      setDoc(d => ({ ...d, entries: d.entries.filter((_, i) => i !== idx) }));
      stopEdit(idx);
      return;
    }
    // revert to original snapshot
    const fallback = orig.entries?.[idx] || {};
    setEntry(idx, fallback); // setEntry merges; we want a full replace:
    setDoc(d => {
      const next = structuredClone(d);
      next.entries[idx] = structuredClone(fallback);
      return next;
    });
    stopEdit(idx);
  };

  const deleteItem = async (idx) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    setSavingIdx(idx);
    setError('');
    try {
      const next = structuredClone(doc);
      next.entries = next.entries.filter((_, i) => i !== idx);
      await api.put('/sections/artists', next);
      setDoc(next);
      setOrig(next);
      // reindex editing map
      setEditingMap({});
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSavingIdx(null);
    }
  };

  const addItem = () => {
    setDoc(d => {
      const next = structuredClone(d);
      next.entries.push({ name:'', role:'', imageUrl:'', website:'', bioParagraphs:[''] });
      return next;
    });
    // put the new last index into edit mode
    setEditingMap(m => ({ ...m, [doc.entries.length]: true }));
    // scroll into view (optional): rely on browser focusing later
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    setError('');
    try {
      await api.put('/sections/artists', doc);
      setOrig(doc);
      setEditingMeta(false);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSavingMeta(false);
    }
  };

  if (loading) return <Spinner/>;

  return (
    <div>
      {/* Section toolbar */}
      <div className="editor-toolbar">
        <div className="d-flex align-items-center gap-2">
          <h4 className="editor-title m-0">Musiker*innen</h4>
          {dirtyAll && <span className="badge-soft">• Änderungen vorhanden</span>}
        </div>
        <div className="d-flex align-items-center gap-2">
          {!editingMeta ? (
            <button className="btn btn-sm btn-edit" onClick={()=>setEditingMeta(true)}>Meta bearbeiten</button>
          ) : (
            <>
              <button className="btn btn-sm btn-save" onClick={saveMeta} disabled={savingMeta}>
                {savingMeta ? 'Speichert…' : 'Meta speichern'}
              </button>
              <button
                className="btn btn-sm btn-cancel"
                onClick={()=>{ setDoc(orig); setEditingMeta(false); }}
                disabled={savingMeta}
              >Abbrechen</button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger m-3 py-2">{error}</div>}

      {/* Section meta */}
      <div className="p-3">
        <div className="row">
          <div className="col-md-6">
            <Text
              label="Titel"
              value={doc.title}
              onChange={(v)=>setDoc(d=>({ ...d, title:v }))}
              readOnly={!editingMeta}
            />
          </div>
        </div>
      </div>

      {/* List header */}
      <div className="d-flex align-items-center justify-content-between px-3 mb-2">
        <strong>Künstler*innen</strong>
        <button type="button" className="btn btn-sm btn-edit" onClick={addItem}>
          <i className="lni-plus me-1" />
        </button>
      </div>

      {/* Cards */}
      <div className="px-3">
        {(doc.entries||[]).map((it, idx) => {
          const editing = !!editingMap[idx];
          const itemDirty = isItemDirty(idx);
          const readOnly = !editing;
          const isSaving = savingIdx === idx;

          return (
            <div key={idx} className="border rounded p-3 mb-3">
              {/* Card toolbar */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-2">
                  <strong>#{idx + 1} — {it.name || 'Ohne Name'}</strong>
                  {editing && itemDirty && <span className="badge-soft">• Nicht gespeichert</span>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!editing ? (
                    <>
                      <button className="icon-btn icon-btn-edit" onClick={()=>startEdit(idx)}>
                        <i className="lni-pencil me-1" aria-hidden="true"/> 
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        onClick={() => deleteItem(idx)}
                        aria-label={`Eintrag ${idx + 1} entfernen`}
                        title="Entfernen"
                      >
                        <i className="lni-trash" aria-hidden="true"></i>
                      </button>
                              </>
                  ) : (
                    <>
                      <button
                        className="btn btn-sm btn-save"
                        onClick={()=>saveItem(idx)}
                        disabled={!itemDirty || isSaving}
                      >
                        {isSaving ? 'Speichert…' : (<><i className="lni-checkmark-circle me-1" /> Speichern</>)}
                      </button>
                      <button className="btn btn-sm btn-cancel" onClick={()=>cancelItem(idx)} disabled={isSaving}>
                        <i className="lni-close me-1" /> Abbrechen
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Fields */}
              <div className="row">
                <div className="col-md-6">
                  <Text label="Name" value={it.name} onChange={(v)=>setEntry(idx,{name:v})} readOnly={readOnly}/>
                </div>
                <div className="col-md-6">
                  <Text label="Rolle" value={it.role} onChange={(v)=>setEntry(idx,{role:v})} readOnly={readOnly}/>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <ImageUploader
                    label="Porträt"
                    value={it.imageUrl}
                    onChange={(url)=>setEntry(idx,{imageUrl:url})}
                    folder="artists"
                    readOnly={readOnly}
                    help="Quadratisches Bild bevorzugt (z.B. 800×800)."
                  />
                </div>
                <div className="col-md-6">
                  <Text label="Website" value={it.website} onChange={(v)=>setEntry(idx,{website:v})} readOnly={readOnly}/>
                </div>
              </div>

              <RepeatingTextArea
                title="Biografie Absätze"
                values={it.bioParagraphs || []}
                readOnly={readOnly}
                onChange={(list)=>setEntry(idx, { bioParagraphs: list })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Small helpers (same look as your other editors) ---------- */

function Spinner(){ return (<div className="p-4 text-center"><div className="spinner-border brand-spinner" role="status" /><div className="small mt-2 text-muted">Lädt…</div></div>); }
function Text({label, value, onChange, readOnly}) {
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input className="form-control" value={value || ''} onChange={e=>onChange(e.target.value)} readOnly={readOnly}/>
    </div>
  );
}

function RepeatingTextArea({ title, values, onChange, readOnly }) {
  const add = () => onChange([...(values||[]), '']);
  const update = (i, v) => onChange(values.map((x, idx) => idx === i ? v : x));
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{title}</strong>
        {!readOnly && (
          <button type="button" className="btn btn-sm btn-edit" onClick={add}>
            <i className="lni-plus me-1" /> Absatz hinzufügen
          </button>
        )}
      </div>
      {(values||[]).map((text, i) => (
        <div key={i} className="border rounded p-2 mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Absatz #{i+1}</strong>
            {!readOnly && (
              <button type="button" className="btn btn-icon" onClick={()=>remove(i)} aria-label="Entfernen" title="Entfernen">
                <i className="lni-close" />
              </button>
            )}
          </div>
          <div className="mb-2">
            <label className="form-label">Text</label>
            <textarea
              className="form-control"
              rows={4}
              value={text || ''}
              onChange={e=>update(i, e.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
